import React, { FC, Fragment, useCallback } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';

import HeaderMenuToggle from 'components/atoms/HeaderMenuToggle';
import Icon from 'components/atoms/Icon';
import { ArrowRightLine } from 'components/icons/System';
import { GO_TO_LINKS, MAIN_NAVIGATION } from 'navigation/mainNavigation';
import Logo from 'components/atoms/Logo';

interface Props {
  setIsMenuOpen: (value: boolean) => void;
  isMenuOpen: boolean;
}

const SideMenu: FC<Props> = ({ setIsMenuOpen, isMenuOpen }) => {
  const handleClick = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const LockFocusWhenOpen = isMenuOpen ? FocusLock : Fragment;
  const RemoveScrollWhenOpen = isMenuOpen ? RemoveScroll : Fragment;

  return (
    <LockFocusWhenOpen>
      <RemoveScrollWhenOpen>
        <div
          className={clsx(
            'side-menu w-full laptop:w-[420px] bg-hrg-red-140 overflow-auto',
            {
              isMenuOpen,
              invisible: !isMenuOpen,
            }
          )}
          data-active={isMenuOpen}
          data-testid="side-menu"
          role="navigation"
          aria-label="side menu"
          aria-hidden={!isMenuOpen}
        >
          <div className="flex flex-col justify-between w-full h-full">
            <div className="mx-8 mt-4 mb-8 tablet:mb-16">
              <div className="flex items-center">
                <HeaderMenuToggle
                  isMenuOpen={isMenuOpen}
                  setIsMenuOpen={setIsMenuOpen}
                />
                <Logo whiteText={true} />
              </div>
              <div className="flex flex-col mt-8 mb-4 tablet:mt-16">
                {MAIN_NAVIGATION.map((link) =>
                  link.isNewTab ? (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mb-4 text-white h2-m"
                    >
                      {link.text}
                    </a>
                  ) : (
                    <Link
                      key={link.href}
                      href={link.href}
                      prefetch={false}
                      legacyBehavior
                    >
                      <a className="mb-4 text-white h2-m" onClick={handleClick}>
                        {link.text}
                      </a>
                    </Link>
                  )
                )}
              </div>
            </div>

            <div className="flex-1 px-8 py-10 bg-hrg-red-90 footer">
              <h2 className="mb-8 text-white h2-m">Go to</h2>
              <div className="flex flex-col">
                {GO_TO_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="flex justify-between w-full mb-4 text-white capitalize h4-m"
                  >
                    {link.text}
                    <Icon icon={ArrowRightLine} fill="text-white" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </RemoveScrollWhenOpen>
    </LockFocusWhenOpen>
  );
};

export default SideMenu;
